// کسورات اسناد هزینه — پنجرهٔ روی ریز سند هزینه
// props: { user, detailId, detailAmount, sendToEvaluation, onClose }
import React, { useState, useEffect, useRef } from 'react';
import { ui } from '../../components/ListPage';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = s => String(s ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const fmtAmt = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');
const val = v => { const m = toEng(v).trim().match(/^-?\d*\.?\d*/); const n = parseFloat(m ? m[0] : ''); return Number.isFinite(n) ? n : 0; };

const API = (path, opts = {}) => fetch(`/api/tadarokat/hazineh-detail${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const SENT_MSG = 'این سند به رسیدگی ارسال شده و امکان تغییر در آن وجود ندارد';
const COLS = [
  { key: 'Title', label: 'عنوان', wide: true }, { key: 'DetailAmount', label: 'مبلغ هزینه', money: true },
  { key: 'DeductionPercent', label: 'درصد' }, { key: 'Deduction', label: 'مبلغ', money: true },
  { key: 'Receiver', label: 'گیرنده', wide: true }, { key: 'AccountNo', label: 'شماره حساب' }, { key: 'BankName', label: 'بانک' },
  { key: 'DeductionKind', label: 'نوع کسور' }, { key: 'IncomeTitle', label: 'عنوان درآمد' }, { key: 'IncomeKind', label: 'نوع درآمد' },
];
const EMPTY = { deduction_id: 0, title: '', deduction: '', deduction_percent: '' };
const hasVal = v => !(v === null || v === undefined || String(v).trim() === '' || String(v).trim() === '0');

function Lookup({ value, disabled, onPick, onClear }) {
  const [text, setText] = useState(value || '');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setText(value || ''); }, [value]);
  const search = (q) => {
    setText(q);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      API(`/deductions/lookup/?q=${encodeURIComponent(q)}`).then(j => { setItems(j && j.ok ? j.data : []); setOpen(true); });
    }, 300);
  };
  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <div style={{ display: 'flex', gap: '4px' }}>
        <input style={{ ...ui.input, ...(disabled ? s.ro : {}) }} value={text} disabled={disabled}
          onChange={e => search(e.target.value)} onFocus={() => !disabled && search(text)}
          onKeyDown={e => { if (!disabled && e.key === 'Delete') onClear(); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)} />
        {!disabled && value && <button type="button" style={s.xBtn} onClick={onClear}>✕</button>}
      </div>
      {open && !disabled && items.length > 0 && (
        <div style={s.drop}>
          {items.map((it, i) => (
            <div key={i} style={s.dropItem} onMouseDown={() => { onPick(it); setOpen(false); }}>{toFa(it.display)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function HazinehDocumentDetailDeduction({ user, detailId, detailAmount = 0, sendToEvaluation = false, onClose }) {
  const fb = useFormButtons('FRMHAZINEHDOCUMENTDETAILDEDUCTION', ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL']);
  const [rows, setRows] = useState([]);
  const [amount, setAmount] = useState(Number(detailAmount) || 0);
  const [sent, setSent] = useState(!!sendToEvaluation);
  const [selId, setSelId] = useState(null);
  const [mode, setMode] = useState('normal');
  const [form, setForm] = useState(EMPTY);
  const [hint, setHint] = useState(false);
  const [msg, setMsg] = useState(null);
  const [closeAfter, setCloseAfter] = useState(false);
  const [askDel, setAskDel] = useState(false);
  const [saving, setSaving] = useState(false);
  const normal = mode === 'normal';
  const open = fb.loaded && fb.open;
  const sel = rows.find(r => r.HazinehDocumentDetailDeductionId === selId) || null;
  const say = (m) => { if (m) setMsg(Array.isArray(m) ? m : [m]); };
  const fill = r => setForm(r ? { deduction_id: r.DeductionId || 0, title: r.Title || '', deduction: r.Deduction ?? '', deduction_percent: r.DeductionPercent ?? '' } : EMPTY);

  const load = (pick) => API(`/items/${detailId}/deductions/`).then(j => {
    if (!j.ok) {
      say(j.msg);
      if (j.closed) setCloseAfter(true);
      return;
    }
    setAmount(Number(j.detail_amount) || 0);
    setSent(!!j.sent);
    const data = j.data || [];
    setRows(data);
    const t = data.find(r => r.HazinehDocumentDetailDeductionId === (pick ?? selId)) || data[0] || null;
    setSelId(t ? t.HazinehDocumentDetailDeductionId : null);
    fill(t);
  });
  useEffect(() => { if (open && detailId) load(); }, [open, detailId]);

  const choose = (r) => { if (!normal) return; setSelId(r.HazinehDocumentDetailDeductionId); fill(r); };
  const startNew = () => { if (sent) { say(SENT_MSG); return; } setForm(EMPTY); setMode('new'); };
  const startEdit = () => { if (!sel) return; if (sent) { say(SENT_MSG); return; } setMode('edit'); };
  const cancel = () => { setMode('normal'); fill(sel); };
  const save = async () => {
    setSaving(true);
    const body = JSON.stringify({ deduction_id: form.deduction_id, deduction: Math.round(val(form.deduction)), deduction_percent: toEng(form.deduction_percent) });
    const j = mode === 'new'
      ? await API(`/items/${detailId}/deductions/create/`, { method: 'POST', body })
      : await API(`/deductions/${selId}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (!j.ok) { say(j.msg); return; }
    setMode('normal');
    load(j.id);
  };
  const askDelete = () => { if (!sel) return; if (sent) { say(SENT_MSG); return; } setAskDel(true); };
  const doDelete = async () => {
    setAskDel(false);
    const j = await API(`/deductions/${selId}/delete/`, { method: 'DELETE' });
    if (!j.ok) { say(j.msg); return; }
    load(null);
  };
  const setPercent = (v) => {
    const p = toEng(v).replace(/[^0-9.]/g, '');
    setForm(f => ({ ...f, deduction_percent: p, deduction: String(amount * val(p) / 100) }));
  };
  const amountLocked = val(form.deduction_percent) > 0;
  const cols = COLS.filter(c => rows.some(r => hasVal(r[c.key])));
  const title = 'کسورات اسناد هزینه';
  const btnOn = (b) => ({ ...b, opacity: sel ? 1 : 0.4, cursor: sel ? 'pointer' : 'default' });

  return (
    <div style={{ ...ui.overlay, zIndex: 1250 }}>
      <div style={{ ...ui.modal, maxWidth: '1000px' }}>
        <div style={ui.mHeader}><span style={ui.mTitle}>{title}</span><button style={ui.closeBtn} onClick={onClose}>✕</button></div>
        {open && (
          <div style={ui.mBody}>
            <div style={ui.row}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: 1 }}>
                <label style={ui.mlabel}>عنوان کسور</label>
                <Lookup value={form.title} disabled={normal}
                  onPick={it => setForm(f => ({ ...f, deduction_id: it.id, title: it.title }))}
                  onClear={() => setForm(f => ({ ...f, deduction_id: 0, title: '' }))} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '100px' }}>
                <label style={ui.mlabel}>درصد</label>
                <input style={{ ...ui.input, ...(normal ? s.ro : {}) }} disabled={normal} value={toFa(form.deduction_percent)}
                  onChange={e => setPercent(e.target.value)} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: '170px' }}>
                <label style={ui.mlabel}>مبلغ کسور</label>
                <input style={{ ...ui.input, ...((normal || amountLocked) ? s.ro : {}) }} readOnly={normal || amountLocked} disabled={normal}
                  value={form.deduction === '' ? '' : Number(Math.round(val(form.deduction))).toLocaleString('fa-IR')}
                  onFocus={() => setHint(!normal && amountLocked)} onBlur={() => setHint(false)}
                  onChange={e => setForm(f => ({ ...f, deduction: toEng(e.target.value).replace(/[^0-9]/g, '') }))} />
                {hint && <span style={{ fontSize: '11px', color: '#c62828' }}>برای وارد کردن مبلغ ابتدا درصد را صفر کنید</span>}
              </div>
            </div>
            <div style={{ ...ui.row, justifyContent: 'flex-end' }}>
              {normal && fb.can('BTNNEW') && <button style={ui.btnNew} onClick={startNew}>جدید</button>}
              {normal && fb.can('BTNEDIT') && <button style={btnOn(ui.btnEdit)} disabled={!sel} onClick={startEdit}>ویرایش</button>}
              {normal && fb.can('BTNDELETE') && <button style={btnOn(ui.btnDel)} disabled={!sel} onClick={askDelete}>حذف</button>}
              {!normal && fb.can('BTNCANCEL') && <button style={ui.btnCancel} onClick={cancel}>لغو</button>}
              {!normal && fb.can('BTNOK') && fb.can(mode === 'new' ? 'BTNNEW' : 'BTNEDIT') && (
                <button style={ui.btnSave} disabled={saving} onClick={save}>{saving ? '⏳' : 'ثبت'}</button>)}
              <ExcelButton title={title} columns={cols} rows={rows} />
            </div>
            <div style={{ overflow: 'auto', maxHeight: '45vh', opacity: normal ? 1 : 0.6, pointerEvents: normal ? 'auto' : 'none' }}>
              <table style={ui.table}>
                <thead><tr>{cols.map(c => <th key={c.key} style={ui.th}>{c.label}</th>)}</tr></thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={cols.length || 1} style={ui.empty}>موردی یافت نشد</td></tr>}
                  {rows.map((r, i) => (
                    <tr key={r.HazinehDocumentDetailDeductionId} onClick={() => choose(r)}
                      style={{ background: r.HazinehDocumentDetailDeductionId === selId ? '#e8f0fe' : (i % 2 ? '#fdf6ec' : '#fff'), cursor: 'pointer' }}>
                      {cols.map(c => (
                        <td key={c.key} style={{ ...(c.money ? ui.tdNum : ui.td), ...(c.wide ? { whiteSpace: 'normal', textAlign: 'right' } : {}) }}>
                          {c.money ? fmtAmt(r[c.key]) : toFa(r[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {askDel && (
        <WarningDialog title="هشدار" lines={['آيا از حذف اين رکورد مطمئن هستيد؟']} confirmLabel="بلی" cancelLabel="خیر" tone="danger"
          onConfirm={doDelete} onCancel={() => setAskDel(false)} />
      )}
      {msg && (
        <WarningDialog title="پیام" lines={msg} confirmLabel="تایید" cancelLabel="بستن"
          onConfirm={() => { setMsg(null); if (closeAfter) onClose(); }} onCancel={() => { setMsg(null); if (closeAfter) onClose(); }} />
      )}
    </div>
  );
}

const s = {
  ro: { background: '#f5f5f5', color: '#333' },
  drop: { position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '8px', maxHeight: '240px', overflowY: 'auto', zIndex: 1300, boxShadow: '0 6px 18px rgba(0,0,0,0.15)' },
  dropItem: { padding: '7px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f3f3f3' },
  xBtn: { padding: '0 10px', background: '#e0e0e0', border: 'none', borderRadius: '8px', cursor: 'pointer' },
};
